export const PartnerApi = {
  url: "https://api.exclusive-networks.cz/portal/api",
  _url: "https://api.exclusive-networks.cz/portal-demo/api",

  /**
   * Základní metoda pro vytvoření HTTP požadavku
   * @param {string} endpoint - Koncový bod API
   * @param {string} method - HTTP metoda (GET, POST, atd.)
   * @param {object} options - Další možnosti jako headers, body, atd.
   * @param {string} token - Autorizační token
   * @returns {Promise<Response>} - Promise s HTTP odpovědí
   */
  request: async (endpoint, method = "GET", options = {}, token = null) => {
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    if (method !== "GET" && !headers["Content-Type"] && options.body) {
      headers["Content-Type"] = "application/json";
      headers["Accepts"] = "application/json";
    }

    const requestOptions = {
      method,
      headers,
    };

    if (options.body && method !== "GET" && method !== "HEAD") {
      requestOptions.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(
        `${PartnerApi.url}${endpoint}`,
        requestOptions
      );

      return response;
    } catch (error) {
      console.error(`Chyba při požadavku na ${endpoint}:`, error);
      throw error;
    }
  },

  /**
   * Základní metoda pro zpracování JSON odpovědi
   * @param {string} endpoint - Koncový bod API
   * @param {string} method - HTTP metoda
   * @param {object} options - Možnosti požadavku
   * @param {string} token - Autorizační token
   * @returns {Promise<any>} - Promise s parsovaným JSON
   */
  requestJSON: async (endpoint, method = "GET", options = {}, token = null) => {
    const response = await PartnerApi.request(endpoint, method, options, token);
    const json = await response.json();

    if (
      parseInt(response.status) === 401 &&
      json?.message === "Expired JWT Token"
    ) {
      localStorage.removeItem("token");
      window.location.href = "/";
      return false;
    }
    return json;
  },

  /**
   * Metoda pro stažení souboru
   * @param {string} endpoint - Koncový bod API
   * @param {string} filename - Název souboru pro stažení
   * @param {string} token - Autorizační token
   * @param {object} options - Další možnosti požadavku
   * @returns {Promise<void>}
   */
  downloadFile: async (endpoint, filename, token, options = {}) => {
    try {
      const response = await PartnerApi.request(
        endpoint,
        options.method ? options.method : "GET",
        options,
        token
      );
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      if (filename == false) {
        return url;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Chyba při stahování:", error);
      throw error;
    }
  },

  // Autentizace
  login: async (credentials) => {
    return PartnerApi.requestJSON("/login", "POST", { body: credentials });
  },

  refreshToken: async (token) => {
    return PartnerApi.requestJSON("/refreshToken", "POST", {}, token);
  },

  passwordRecovery: async (username) => {
    return PartnerApi.requestJSON(
      `/passwordRecovery/${encodeURIComponent(username)}`,
      "GET",
      {}
    );
  },

  passwordRecoveryCode: async ({ username, data }) => {
    return PartnerApi.requestJSON(
      `/passwordRecovery/${encodeURIComponent(username)}`,
      "PATCH",
      { body: data }
    );
  },

  me: async (token) => {
    return PartnerApi.requestJSON("/me", "GET", {}, token);
  },

  contacts: async ({ token }) => {
    return PartnerApi.requestJSON("/contacts", "GET", {}, token);
  },

  usersRoleSchema: async ({ token }) => {
    return PartnerApi.requestJSON(`/users/roleSchema`, "GET", {}, token);
  },

  users: async ({ token }) => {
    return PartnerApi.requestJSON(`/users/list`, "GET", {}, token);
  },

  user: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/user/${id}`, "GET", {}, token);
  },

  registerUser: async ({ company_ic, login }) => {
    return PartnerApi.requestJSON("/registration", "POST", {
      body: { company_ic, login },
    });
  },

  userAdd: async ({ token, data }) => {
    return PartnerApi.requestJSON(
      "/users/addUser",
      "POST",
      { body: data },
      token
    );
  },

  userDelete: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/user/${id}`, "DELETE", {}, token);
  },

  userAddUsername: async ({ token, person_id, username, company_branch_id }) => {
    return PartnerApi.requestJSON(
      `/user/${person_id}/username/${username}`,
      "POST",
        {body: {company_branch_id}},
      token
    );
  },

  userDeleteUsername: async ({ token, person_id, username, company_branch_id }) => {
    return PartnerApi.requestJSON(
      `/user/${person_id}/username/${username}`,
      "DELETE",
      {body: {company_branch_id}},
      token
    );
  },

  updateRoleMultipleUsers: async ({ token, data }) => {
    return PartnerApi.requestJSON(
      `/user/updateRoleMultipleUsers`,
      "PATCH",
      { body: data },
      token
    );
  },

  userRoleChange: async ({ token, person_id, role_id, value, company_branch_id }) => {
    return PartnerApi.requestJSON(
      `/user/${person_id}/updateRole/${role_id}/${value}`,
      "PATCH",
      {body: {company_branch_id}},
      token
    );
  },

  // Events
  events: async ({ token, data }) => {
    return PartnerApi.requestJSON("/event/list", "POST", { body: data }, token);
  },

  event: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/event/${id}`, "GET", {}, token);
  },

  // Loans
  loanAdd: async ({ token, data }) => {
    return PartnerApi.requestJSON("/loan", "POST", { body: data }, token);
  },

  loanItemAdd: async ({ token, data, id }) => {
    return PartnerApi.requestJSON(
      `/loan/${id}/addItem`,
      "POST",
      { body: data },
      token
    );
  },

  loanItemDelete: async ({ token, id, item_id }) => {
    return PartnerApi.requestJSON(
      `/loan/${id}/${item_id}`,
      "DELETE",
      {},
      token
    );
  },

  loan: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/loan/${id}`, "GET", {}, token);
  },

  loans: async ({ token, data, page }) => {
    return PartnerApi.requestJSON(
      `/loan/list/${page}`,
      "POST",
      { body: data },
      token
    );
  },

  loanVendors: async ({ token }) => {
    return PartnerApi.requestJSON("/loan/hwToLoan", "GET", {}, token);
  },

  loanVendorProducts: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/loan/hwToLoan/${id}`, "GET", {}, token);
  },

  loanVendorProductAvailability: async ({
    token,
    vendor_id,
    product_id,
    date_from,
    date_to,
  }) => {
    return PartnerApi.requestJSON(
      `/loan/hwToLoan/${vendor_id}/${product_id}/${date_from}/${date_to}`,
      "GET",
      {},
      token
    );
  },

  loansExport: async ({ token, data }) => {
    return PartnerApi.downloadFile(
      "/loan/exportXLSX",
      "export-loans.xlsx",
      token,
      {
        method: "POST",
        body: data,
      }
    );
  },

  // Vendors
  vendors: async ({ token }) => {
    return PartnerApi.requestJSON("/vendor/list", "GET", {}, token);
  },

  vendorsWarehouseSale: async ({ token }) => {
    return PartnerApi.requestJSON(
      "/vendor/listWarehouseSale",
      "GET",
      {},
      token
    );
  },

  vendor: async ({ token, vendorId }) => {
    return PartnerApi.requestJSON(`/vendor/${vendorId}`, "GET", {}, token);
  },

  vendorProductSets: async ({ token, id, directory_id }) => {
    return PartnerApi.requestJSON(
      `/vendor/${id}/productSets/${directory_id}`,
      "GET",
      {},
      token
    );
  },

  vendorProductOverview: async ({ token, id }) => {
    return PartnerApi.requestJSON(
      `/vendor/${id}/productOverview`,
      "GET",
      {},
      token
    );
  },

  vendorDocuments: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/vendor/${id}/documents`, "GET", {}, token);
  },

  vendorWarehouseSales: async ({ token, id }) => {
    return PartnerApi.requestJSON(
      `/vendor/${id}/warehouseSale`,
      "GET",
      {},
      token
    );
  },

  vendorProducts: async ({ token, vendor_id, directory_id }) => {
    return PartnerApi.requestJSON(
      `/product/list/${vendor_id}/${directory_id}`,
      "GET",
      {},
      token
    );
  },

  // Shipping

  shippings: async ({ token }) => {
    return PartnerApi.requestJSON("/shipping/list", "GET", {}, token);
  },

  shipping: async ({ token, shipping_company, tracking_id }) => {
    return PartnerApi.requestJSON(
      `/shipping/${shipping_company}/${tracking_id}`,
      "GET",
      {},
      token
    );
  },

  // Invoices
  invoices: async ({ token, page, rowsSelected, filter, sortTable }) => {
    const data = {
      rows_per_page: rowsSelected,
      sort: sortTable.sort,
      sort_direction: sortTable.sortDirection,
      filter: filter,
    };

    return PartnerApi.requestJSON(
      `/invoice/list/${page}`,
      "POST",
      { body: data },
      token
    );
  },

  invoicesExport: async ({ token, rowsSelected, filter }) => {
    const data = {
      rows_per_page: rowsSelected,
      sort: "invoice_number",
      sort_direction: "desc",
      filter: filter,
    };

    return PartnerApi.downloadFile(
      "/invoice/exportXLSX",
      "export.xlsx",
      token,
      {
        method: "POST",
        body: data,
      }
    );
  },

  // Opportunities
  opportunities: async ({ token, page, filter, rowsSelected, sortTable }) => {
    const data = {
      rows_per_page: rowsSelected,
      sort: sortTable?.sort ? sortTable.sort : "opportunity_id",
      sort_direction: sortTable?.sortDirection
        ? sortTable.sortDirection
        : "desc",
      filter,
    };

    return PartnerApi.requestJSON(
      `/opportunity/list/${page}`,
      "POST",
      { body: data },
      token
    );
  },

  opportunitiesExport: async ({ token, filter }) => {
    return PartnerApi.downloadFile(
      "/opportunity/exportXLSX",
      "export.xlsx",
      token,
      {
        method: "POST",
        body: { filter },
      }
    );
  },

  glsPackagesList: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/opportunity/${id}/packing-list`, "GET", {}, token);
  },

  glsPackagesListPDF: async ({ token, id }) => {
    return PartnerApi.downloadFile(
        `/opportunity/${id}/packing-list/pdf`,
        `packing-list-${id}.pdf`,
        token
    );
  },

  glsPackagePodPDF: async ({ token, trackingId, id }) => {
    return PartnerApi.downloadFile(
        `/opportunity/${id}/pod/${trackingId}/pdf`,
        `pod-${trackingId}.pdf`,
        token
    );
  },

  glsDeliveryListPDF: async ({ token, id }) => {
    return PartnerApi.downloadFile(
        `/opportunity/${id}/delivery-list/pdf`,
        `deliverylist-${id}.pdf`,
        token
    );
  },

  opportunity: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/opportunity/${id}`, "GET", {}, token);
  },

  opportunityPlaceOrder: async ({ token, id }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${id}/placeOrder`,
      "GET",
      {},
      token
    );
  },
  opportunityCancelOpportunity: async ({ token, id }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${id}/cancelOpportunity`,
      "GET",
      {},
      token
    );
  },

  opportunityAdd: async ({ token, description, company_id, items = null, company_branch_id=null }) => {
    return PartnerApi.requestJSON(
      "/opportunity",
      "POST",
      {
        body: { description, company_id, items, company_branch_id },
      },
      token
    );
  },

  opportunityEdit: async ({ token, id, data }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${id}`,
      "PATCH",
      {
        body: data,
      },
      token
    );
  },

  opportunityShipping: async ({ token, id }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${id}/shipping`,
      "GET",
      {},
      token
    );
  },

  opportunityVariantAdd: async ({ token, description, opportunity_id }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${opportunity_id}/variation`,
      "POST",
      {
        body: { description },
      },
      token
    );
  },

  opportunityVariantProductAdd: async ({
    token,
    variation_id,
    product_id,
    quantity,
    opportunity_id,
  }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${opportunity_id}/variation/${variation_id}/item`,
      "POST",
      {
        body: { product_id, quantity, description: "" },
      },
      token
    );
  },

  opportunityVariantProductAddItems: async ({
    token,
    variation_id,
    items,
    opportunity_id,
  }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${opportunity_id}/variation/${variation_id}/items`,
      "POST",
      {
        body: { items },
      },
      token
    );
  },

  opportunityVariantProductChange: async ({
    token,
    variation_id,
    item_id,
    quantity,
    opportunity_id,
  }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${opportunity_id}/variation/${variation_id}/item/${item_id}`,
      "PATCH",
      {
        body: { quantity: parseInt(quantity) },
      },
      token
    );
  },

  opportunityVariantProductDelete: async ({
    token,
    variation_id,
    item_id,
    opportunity_id,
  }) => {
    return PartnerApi.requestJSON(
      `/opportunity/${opportunity_id}/variation/${variation_id}/item/${item_id}`,
      "DELETE",
      {},
      token
    );
  },

  // Deals
  deals: async ({ token, page, filter, rowsSelected, sortTable }) => {
    const data = {
      rows_per_page: rowsSelected,
      sort: sortTable.sort,
      sort_direction: sortTable.sortDirection,
      filter: filter,
    };

    return PartnerApi.requestJSON(
      `/deal/list/${page}`,
      "POST",
      { body: data },
      token
    );
  },

  dealsExport: async ({ token, filter, rowsSelected, sortTable }) => {
    const data = {
      rows_per_page: rowsSelected,
      sort: sortTable.sort,
      sort_direction: sortTable.sortDirection,
      filter: filter,
    };

    return PartnerApi.downloadFile("/deal/exportXLSX", "export.xlsx", token, {
      method: "POST",
      body: data,
    });
  },

  deal: async ({ token, id }) => {
    return PartnerApi.requestJSON(`/deal/${id}`, "GET", {}, token);
  },

  dealAdd: async ({ token, data }) => {
    return PartnerApi.requestJSON("/deal", "POST", { body: data }, token);
  },

  dealChange: async ({ token, data }) => {
    return PartnerApi.requestJSON("/deal", "PATCH", { body: data }, token);
  },

  // Comments
  commentAdd: async ({ token, data }) => {
    return PartnerApi.requestJSON(
      "/comment/add",
      "POST",
      { body: data },
      token
    );
  },

  // Companies
  companySearch: async ({ token, search }) => {
    return PartnerApi.requestJSON(
      "/company/search",
      "POST",
      {
        body: { search },
      },
      token
    );
  },

  company: async ({ token, company_id }) => {
    return PartnerApi.requestJSON(`/company/${company_id}`, "GET", {}, token);
  },

  companyList: async ({ token }) => {
    return PartnerApi.requestJSON(`/company/list`, "GET", {}, token);
  },

  companyPersons: async ({ token, company_id }) => {
    return PartnerApi.requestJSON(
      `/company/${company_id}/persons`,
      "GET",
      {},
      token
    );
  },

  companyDeliveryAddressAdd: async ({ token, company_id, data }) => {
    return PartnerApi.requestJSON(
      `/company/${company_id}/deliveryAddress`,
      "POST",
      { body: data },
      token
    );
  },

  companyDeliveryAddressUpdate: async ({
    token,
    company_id,
    address_id,
    data,
  }) => {
    return PartnerApi.requestJSON(
      `/company/${company_id}/deliveryAddress/${address_id}`,
      "PATCH",
      { body: data },
      token
    );
  },

  companyDeliveryAddressDelete: async ({ token, company_id, address_id }) => {
    return PartnerApi.requestJSON(
      `/company/${company_id}/deliveryAddress/${address_id}`,
      "DELETE",
      {},
      token
    );
  },

  companyCompanyBranchAdd: async ({ token, company_id, data }) => {
    return PartnerApi.requestJSON(
        `/company/${company_id}/branch`,
        "POST",
        { body: data },
        token
    );
  },

  companyCompanyBranchAddPerson: async ({ token, company_branch_id, person_id, company_id }) => {
    return PartnerApi.requestJSON(
        `/company/${company_id}/branchPerson`,
        "POST",
        { body: {company_branch_id, person_id} },
        token
    );
  },

  companyCompanyBranchDeletePerson: async ({ token, company_branch_id, person_id, company_id }) => {
    return PartnerApi.requestJSON(
        `/company/${company_id}/branchPerson`,
        "DELETE",
        { body: {company_branch_id, person_id} },
        token
    );
  },

  companyCompanyBranchUpdate: async ({
                                         token,
                                         company_id,
                                       company_branch_id,
                                         data,
                                       }) => {
    return PartnerApi.requestJSON(
        `/company/${company_id}/branch/${company_branch_id}`,
        "PATCH",
        { body: data },
        token
    );
  },

  companyCompanyBranchDelete: async ({ token, company_id, company_branch_id }) => {
    return PartnerApi.requestJSON(
        `/company/${company_id}/branch/${company_branch_id}`,
        "DELETE",
        {},
        token
    );
  },


  // Products
  productSearch: async ({ token, search }) => {
    return PartnerApi.requestJSON(
      "/product/search",
      "POST",
      {
        body: { search },
      },
      token
    );
  },

  // Renewals
  renewalListCustomers: async ({ token, filter }) => {
    return PartnerApi.requestJSON(
      "/renewal/listByCustomer",
      "POST",
      { body: filter },
      token
    );
  },

  renewalListByCustomer: async ({ token, company_id, filter }) => {
    return PartnerApi.requestJSON(
      `/renewal/listByCustomer/${company_id}`,
      "GET",
      { body: filter },
      token
    );
  },

  renewalListByQuarter: async ({ token, quarter, filter }) => {
    return PartnerApi.requestJSON(
      `/renewal/listByQuarter/${quarter}`,
      "POST",
      { body: filter },
      token
    );
  },

  renewalOffer: async ({ token, company_id, expire_date }) => {
    return PartnerApi.requestJSON(
      `/renewal/renewalOffer/${company_id}/${expire_date}`,
      "GET",
      {},
      token
    );
  },

  renewalOfferAdd: async ({ token, company_id, expire_date, items }) => {
    return PartnerApi.requestJSON(
      `/renewal/renewalOffer/${company_id}/${expire_date}`,
      "POST",
      { body: items },
      token
    );
  },

  renewalOfferDelete: async ({ token, company_id, expire_date, items }) => {
    return PartnerApi.requestJSON(
      `/renewal/renewalOffer/${company_id}/${expire_date}`,
      "DELETE",
      { body: items },
      token
    );
  },

  renewalsExport: async ({ token }) => {
    return PartnerApi.downloadFile(
      "/renewal/exportXLSX",
      "export-renewals.xlsx",
      token
    );
  },

  download: async (url, filename, token) => {
    return PartnerApi.downloadFile(url, filename, token);
  },
  dashboard: async ({ token }) => {
    return PartnerApi.requestJSON(`/dashboard`, "GET", {}, token);
  },
  yearStats: async ({ token, year }) => {
    return PartnerApi.requestJSON(`/salesStats/${year}`, "GET", {}, token);
  },
  yearCompanyStats: async ({ token, year, company_id }) => {
    return PartnerApi.requestJSON(
      `/salesStats/${year}/${company_id}`,
      "GET",
      {},
      token
    );
  },
  overallStats: async ({ token }) => {
    return PartnerApi.requestJSON(`/salesStats/allYears`, "GET", {}, token);
  },
  overallCompanyStats: async ({ token, company_id }) => {
    return PartnerApi.requestJSON(
      `/salesStats/allYears/${company_id}`,
      "GET",
      {},
      token
    );
  },

  //Sold SN
  soldSNLetters: async ({ token }) => {
    return PartnerApi.requestJSON(`/opportunity/soldSN`, "GET", {}, token);
  },
  soldSNCompanies: async ({ token, letter }) => {
    return PartnerApi.requestJSON(
      `/opportunity/soldSN/${letter}`,
      "GET",
      {},
      token
    );
  },
  soldSNCompany: async ({ token, letter, company_id }) => {
    return PartnerApi.requestJSON(
      `/opportunity/soldSN/${letter}/${company_id}`,
      "GET",
      {},
      token
    );
  },
  soldSNSearch: async ({ token, sn }) => {
    return PartnerApi.requestJSON(
      `/opportunity/soldSN/search/${sn}`,
      "GET",
      {},
      token
    );
  },
  soldSNExport: async ({ token }) => {
    return PartnerApi.downloadFile(
      "/opportunity/soldSN/exportXLSX",
      "export-sold-sn.xlsx",
      token,
      {
        method: "GET",
      }
    );
  },

  unregisteredFTN: async ({ token }) => {
    return PartnerApi.requestJSON(
      "/fortinet/unregisteredLicenses",
      "GET",
      {},
      token
    );
  },
};
